import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DanyAvatar, useViewport } from "@/ds";
import { useDanyUi } from "@/components/dany/DanyUiContext";
import { fabSuppressedOn, modeForPath } from "@/lib/danyPageMode";

/**
 * Dany's floating entry point.
 *
 * Two rules, both from Jorge's DY1.4 spec:
 *
 *   1. On a page WITH the DanyHome hero, the FAB exists only once the hero's
 *      CONVERSATIONAL BOX has left the viewport, and goes away again on the way
 *      back up. Two doors to the same room, never both open at once — and the
 *      door that matters is the composer, not the section around it. See
 *      DanyUiContext for why that distinction cost the phone a few hundred
 *      pixels of scrolling.
 *   2. On every other page it is always there, opening in that page's context
 *      mode (DANY.md §2.5, point 4).
 *
 * Clicking it navigates to the SAME full-screen surface the hero composer opens
 * (`/dany`). There is no alternate panel: one conversational surface, two doors.
 *
 * Placement is bottom-right, and from T12-M1 that is a DESKTOP-only statement:
 * below 992px this button does not render at all, because Dany lives in the
 * centre of the mobile tab bar. The `safe-area-inset-bottom` offset stays for
 * the tablet widths between 992px and the desktop layout.
 */
export default function DanyFab() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasConversationBox, conversationBoxVisible } = useDanyUi();
  const { isNavMobile } = useViewport();
  const [hover, setHover] = React.useState(false);

  const suppressed = fabSuppressedOn(location.pathname);
  // Shown when there is no conversational box to compete with, or when that box
  // has scrolled out of view.
  //
  // T12-M1: and only from 992px up. Below that the bottom tab bar carries Dany
  // in its centre cell, and two doors to the same room — one of them floating
  // over the other — is exactly what the bar was drawn to end. The bar's FAB
  // opens the same surface in the same page mode; this one keeps the desktop
  // corner it has always had.
  const shown =
    !isNavMobile && !suppressed && (!hasConversationBox || !conversationBoxVisible);

  // Kept mounted and faded so the transition runs in both directions; a
  // conditional unmount would make it pop back in with no animation.
  return (
    <button
      type="button"
      onClick={() => navigate("/dany", { state: { mode: modeForPath(location.pathname) } })}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label={t("dany.fab.label")}
      title={t("dany.fab.label")}
      aria-hidden={!shown}
      tabIndex={shown ? 0 : -1}
      data-testid="dany-fab"
      data-shown={shown ? "true" : "false"}
      style={{
        position: "fixed",
        right: "clamp(16px, 4vw, 24px)",
        bottom: "calc(clamp(20px, 5vw, 24px) + env(safe-area-inset-bottom, 0px))",
        zIndex: 45,
        display: "inline-flex",
        alignItems: "center",
        gap: "var(--space-2)",
        height: 56,
        minWidth: 56,
        padding: hover ? "0 20px 0 12px" : "0 12px",
        border: "1.5px solid var(--worker)",
        borderRadius: "var(--radius-pill)",
        background: "var(--action-primary)",
        color: "var(--text-on-primary)",
        fontFamily: "var(--font-display)",
        fontWeight: "var(--weight-semibold)",
        fontSize: "var(--text-sm)",
        whiteSpace: "nowrap",
        cursor: "pointer",
        boxShadow: "var(--shadow-deep)",
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0) scale(1)" : "translateY(12px) scale(0.9)",
        pointerEvents: shown ? "auto" : "none",
        transition:
          "opacity var(--duration-slow) var(--ease-out), transform var(--duration-slow) var(--ease-out), padding var(--duration-base) var(--ease-out)",
      }}
    >
      <DanyAvatar size={32} state="idle" halo={false} />
      {hover && <span>{t("dany.fab.cta")}</span>}
    </button>
  );
}
